import { Component, Inject, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  ProfileVideo,
  VIDEO_STATUS_LABELS,
  formatVideoDuration,
  isPlayableVideo,
} from 'src/app/models/profile-video.model';

export interface VideoPlayerDialogData {
  /** Every video on the profile, so the dialog can step through them. */
  videos: ProfileVideo[];
  /** Which one the admin clicked. */
  index: number;
  /** Shown in the title bar, so a player opened from a table still has a name on it. */
  title?: string;
}

/**
 * Full-size player for one profile video, with sound and controls.
 *
 * The table's tiles preview muted; this is where an admin actually watches a
 * video to judge it, so it opens large, unmuted and with the browser's own
 * controls (scrubbing and volume are the whole point). Arrow keys and the
 * chevrons step through the rest of the profile's videos without closing.
 */
@Component({
  selector: 'sa-video-player-dialog',
  templateUrl: './video-player-dialog.component.html',
  styleUrls: ['./video-player-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
})
export class VideoPlayerDialogComponent {
  @ViewChild('player') player: ElementRef<HTMLVideoElement>;

  videos: ProfileVideo[];
  index = 0;
  title = '';

  constructor(@Inject(MAT_DIALOG_DATA) data: VideoPlayerDialogData) {
    this.videos = (data && data.videos) || [];
    this.index = Math.min(Math.max(data && data.index ? data.index : 0, 0), Math.max(this.videos.length - 1, 0));
    this.title = (data && data.title) || '';
  }

  get video(): ProfileVideo | null {
    return this.videos[this.index] || null;
  }

  get playable(): boolean {
    return isPlayableVideo(this.video);
  }

  /**
   * MP4 first: every browser plays it, while HLS is native only in Safari and
   * the admin app ships no HLS library. The manifest is the fallback for the
   * rare row that has one and no progressive file.
   */
  get source(): string {
    const video = this.video;
    if (!video) return '';
    return video.mp4_url || video.hls_url || '';
  }

  get statusLabel(): string {
    return this.video ? VIDEO_STATUS_LABELS[this.video.status] || this.video.status : '';
  }

  get duration(): string {
    return formatVideoDuration(this.video && this.video.duration_ms);
  }

  get dimensions(): string {
    const video = this.video;
    if (!video || !video.width || !video.height) return '';
    return `${video.width}×${video.height}`;
  }

  get hasMultiple(): boolean {
    return this.videos.length > 1;
  }

  step(delta: number): void {
    if (!this.hasMultiple) return;
    const next = (this.index + delta + this.videos.length) % this.videos.length;
    this.index = next;
    // The <video> keeps the old buffered stream unless it is told to reload.
    if (this.player && this.player.nativeElement) {
      this.player.nativeElement.load();
    }
  }
}
